/**
 * Python Post-Execution Hooks
 *
 * Sets up hooks that run after Python code execution to translate snapshot
 * diffs into bridge operations. The shared committed-change funnel owns worker
 * sync, compilation, and overview refresh after the resulting Yjs commit.
 */
import { Logger } from './logger';
import { createNamedChangePairFromJsonPatchPair, createSyntheticChangeOperationsFromNamedChangePairs } from './collaboration-message';
import { getCommittedChangeRefreshPromise } from './change-bridge-init';
import { isAssistantPythonExecutionActive, setActiveAssistantPythonExecutionCommit } from './assistant-execution-context';
import { diffFontDataToPatchPairs } from './font-data-diff';
const console = new Logger('PythonPostExecution');
function toJsonPointerPath(path) {
    if (!path.length) {
        return '';
    }
    return `/${path
        .map((segment) => String(segment).replaceAll('~', '~0').replaceAll('/', '~1'))
        .join('/')}`;
}
function diffFontDataToJsonPatchPairs(beforeValue, afterValue) {
    return diffFontDataToPatchPairs(beforeValue, afterValue).map(({ forward, inverse }) => ({
        forward: {
            op: forward.op,
            path: toJsonPointerPath(forward.path),
            value: forward.value
        },
        inverse: {
            op: inverse.op,
            path: toJsonPointerPath(inverse.path),
            value: inverse.value
        }
    }));
}
function createNamedPatchPairsFromJsonSnapshots(beforeSnapshot, afterSnapshot, workerReplayTargets) {
    const jsonPatchPairs = diffFontDataToJsonPatchPairs(beforeSnapshot, afterSnapshot);
    return jsonPatchPairs.map((patchPair) => createNamedChangePairFromJsonPatchPair(patchPair.forward, patchPair.inverse, {
        forwardSnapshot: patchPair.forward.op === 'remove'
            ? beforeSnapshot
            : afterSnapshot,
        inverseSnapshot: patchPair.inverse.op === 'remove'
            ? afterSnapshot
            : beforeSnapshot,
        replayOldValue: patchPair.inverse.op === 'remove'
            ? undefined
            : patchPair.inverse.value,
        replayNewValue: patchPair.forward.op === 'remove'
            ? undefined
            : patchPair.forward.value,
        workerReplayTargets
    }));
}
function createCanonicalSerializedFontSnapshot(currentFont) {
    if (!currentFont?.babelfontJson) {
        return null;
    }
    return JSON.parse(currentFont.babelfontJson);
}
export function commitPythonExecutionSyntheticChanges(currentFont, historyContext, bridge, outcome = { succeeded: true }) {
    let didApplyOperations = false;
    const releaseRecordingSuppression = () => {
        if (historyContext?.releaseRecordingSuppression) {
            historyContext.releaseRecordingSuppression();
        }
        else {
            bridge?.setRecordingSuppressed?.(false);
        }
    };
    try {
        if (!currentFont) {
            return;
        }
        // Python mutates the live wrapper/model graph directly. Canonicalize that
        // already-committed state in place, refresh the serialized snapshot, then
        // derive the authoritative synthetic diff from that post-sync serialization.
        currentFont.syncJsonFromModel();
        const beforeFontDataJson = historyContext?.beforeFontDataJson;
        if (!beforeFontDataJson || !bridge) {
            return;
        }
        const afterSnapshot = createCanonicalSerializedFontSnapshot(currentFont);
        if (!afterSnapshot) {
            return;
        }
        const beforeSnapshot = JSON.parse(beforeFontDataJson);
        const directOperations = createSyntheticChangeOperationsFromNamedChangePairs(createNamedPatchPairsFromJsonSnapshots(beforeSnapshot, afterSnapshot, []));
        if (directOperations.length) {
            // Live Python setters remain suppressed, but the canonical diff is
            // the one intentional bridge operation that must be recorded.
            releaseRecordingSuppression();
            const editSource = isAssistantPythonExecutionActive()
                ? 'assistant'
                : 'python';
            bridge.applySyntheticChangeSet(historyContext?.label ?? 'Python script', directOperations.map((operation) => ({
                ...operation,
                editSource
            })));
            didApplyOperations = true;
        }
    }
    finally {
        if (historyContext?.transactionStarted) {
            releaseRecordingSuppression();
            const commitResult = bridge?.endTransaction();
            if (didApplyOperations && commitResult) {
                window.dispatchEvent(new CustomEvent('fontModelSync'));
            }
            if (didApplyOperations &&
                commitResult &&
                isAssistantPythonExecutionActive()) {
                setActiveAssistantPythonExecutionCommit(outcome.succeeded ? 'committed' : 'partial', getCommittedChangeRefreshPromise());
            }
        }
    }
}
console.log('🔧 Module loaded, setting up post-execution hooks...');
// Wait for required globals to be available
function setupHooks() {
    if (window.__counterpunchPythonPostExecutionHookInstalled) {
        return;
    }
    console.log('[PythonPostExec]', 'setupHooks called, checking for autoCompileManager:', !!window.autoCompileManager);
    if (!window.autoCompileManager) {
        console.log('[PythonPostExec]', 'Waiting for autoCompileManager...');
        setTimeout(setupHooks, 500);
        return;
    }
    console.log('[PythonPostExec]', '✅ autoCompileManager found, installing afterPythonExecution hook...');
    // Save any existing hook so we can call it too (chaining)
    const existingHook = window.afterPythonExecution;
    console.log('[PythonPostExec]', '   Existing hook:', typeof existingHook === 'function' ? 'found' : 'none');
    /**
     * Hook that runs after every Python code execution
     * Triggers font recompilation
     */
    window.afterPythonExecution = async function (outcome = { succeeded: true }) {
        try {
            commitPythonExecutionSyntheticChanges(window.fontManager?.currentFont, window.pythonExecutionHistoryContext, window.patchSyncEngine, outcome);
        }
        finally {
            window.pythonExecutionHistoryContext?.releaseRecordingSuppression?.();
            window.pythonExecutionHistoryContext = null;
        }
        if (typeof existingHook === 'function') {
            await existingHook(outcome);
        }
    };
    window.__counterpunchPythonPostExecutionHookInstalled = true;
    console.log('[PythonPostExec]', '✅ Post-execution hooks installed successfully');
    console.log('[PythonPostExec]', 'window.afterPythonExecution is now:', typeof window.afterPythonExecution);
}
// Start setup when DOM is ready
console.log('[PythonPostExec]', 'Document ready state:', document.readyState);
if (document.readyState === 'loading') {
    console.log('[PythonPostExec]', 'Adding DOMContentLoaded listener...');
    document.addEventListener('DOMContentLoaded', setupHooks);
}
else {
    console.log('[PythonPostExec]', 'DOM already ready, calling setupHooks immediately...');
    setupHooks();
}
console.log('[PythonPostExec]', '📦 Module initialization complete');
// Immediately invoke setup to ensure this code runs
// (prevents webpack from tree-shaking this module)
(function () {
    console.log('[PythonPostExec]', '🚀 IIFE executing...');
})();
